import { sortBy, uniqBy } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchModelEntityDataInBulkOrThrow } from '@upright/ui/api/model/output/scores/model-entity-data';

type FetchComparisonDataQueryParams = Parameters<typeof fetchModelEntityDataInBulkOrThrow>[0];

export function generateGetComparisonDataQueryKey(params?: FetchComparisonDataQueryParams) {
  return ['get-comparison-data', ...(params ? [params] : [])] as const;
}

const defaultOptions: FetchComparisonDataQueryParams['options'] = {
  includeRating: undefined,
  scenarioId: undefined,
  startDate: undefined,
  endDate: undefined,
};

export function useGetComparisonDataQuery(params: FetchComparisonDataQueryParams, { enabled } = { enabled: true }) {
  const {
    payload: { entities, version, release },
    options: optionsFromArgs,
  } = params;
  const uniqRefs = uniqBy(entities, d => `${d.type}${d.id}`);
  // OH: Changing the order of refs in the list should not cause a refetch
  const orderedRefs = sortBy(uniqRefs, ref => `${ref.type}${ref.id}${ref.resultId}`);
  const searchableRefs = orderedRefs.map(ref => {
    return {
      id: ref.id,
      type: ref.type,
    };
  });
  const options = { ...defaultOptions, ...optionsFromArgs };
  const payload = { entities: searchableRefs, version, release };

  return useQuery({
    queryKey: generateGetComparisonDataQueryKey({
      payload,
      options,
    }),
    queryFn: async () => {
      if (version) {
        return await fetchModelEntityDataInBulkOrThrow({
          payload,
          options,
        });
      }
      return [];
    },
    refetchOnWindowFocus: false,
    staleTime: 10 * 1000,
    gcTime: Infinity,
    placeholderData: keepPreviousData,
    enabled,
  });
}
